import { Component, OnInit, OnDestroy, inject, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DarkModeService } from '../../services/dark-mode.service';
import { environment } from '../../../environments/environment';
import { StorageService } from '../../services/storage.service';
import {AppBuild} from "../../common/interfaces/global-interfaces";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  form: FormGroup;
  hide = true;
  loading = false;
  loginStep: 'credentials' | 'mfa' | 'forgot' = 'credentials';

  // Forgot password
  forgotEmail = '';
  forgotLoading = false;
  forgotSuccess = false;

  constructor(){  }

  PRIMARY = '#02607f';
  SECONDARY = '#009688';

  slides = [
    {
      title: 'Government ICT Service Portal',
      subtitle: 'Welcome to the',
      desc: 'A one-stop window for Government ICT services, projects, and technical support provided by the e-Government Authority (e-GA) to public institutions.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1600',
    },
    {
      title: 'Unified Digital Services',
      subtitle: 'Seamlessly Connected',
      desc: 'Access integrated public sector platforms, e-services, and government systems from one secure, centralised portal.',
      image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=1600',
    },
    {
      title: 'Data Security & Compliance',
      subtitle: 'Protected at Every Layer',
      desc: 'ISO/IEC 27001:2022 certified infrastructure ensuring your data is governed by world-class security standards round the clock.',
      image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?q=80&w=1600',
    },
    {
      title: '24/7 Technical Support',
      subtitle: 'Always Available',
      desc: 'Our dedicated ICT support teams ensure government systems are operational with 99.9% uptime SLA and rapid incident response.',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=1600',
    },
  ];
  index = 0;

  pulses = [
    { top: '10%', left: '20%' },
    { top: '30%', left: '50%' },
    { top: '60%', left: '80%'    },
    { icon: 'shield_locked', color: 'text-indigo-400', top: '70%', left: '80%', delay: '4.5s' }
  ];

  isAccessibilityMenuOpen = false;

  // Scroll-aware nav (mirrors GuestMainComponent)
  scrolled = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.scrolled = window.scrollY > 20;
  }

  // Theme state
  isLightMode = true;

  // Timeout
  isTimeout = false;
  timeoutMessage = '';

  appBuild: AppBuild = environment.SYSTEM_BUILD;

  private fb: FormBuilder = inject(FormBuilder);
  private authService: AuthService = inject(AuthService);
  private notificationService: NotificationService = inject(NotificationService);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private storageService: StorageService = inject(StorageService);
  private darkModeService: DarkModeService = inject(DarkModeService);

  ngOnInit(): void {
    this.form = this.fb.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
    });

    // Initialize dark mode
    this.isLightMode = !this.darkModeService.isDarkMode;

    // Handle session timeout
    this.route.queryParams.subscribe((params) => {
      if (params['timeout'] === 'true') {
        this.isTimeout = true;
        const timeoutMs = environment.SESSION_TIMEOUT || 300000;
        const minutes = Math.floor(timeoutMs / 60000);
        const hours = Math.floor(minutes / 60);
        if (hours > 0) {
          const remainingMinutes = minutes % 60;
          this.timeoutMessage =
            remainingMinutes > 0
              ? `Your session timed out due to ${hours} hour${
                hours > 1 ? 's' : ''
              } and ${remainingMinutes} minute${
                remainingMinutes > 1 ? 's' : ''
              } of inactivity. You have been logged out.`
              : `Your session timed out due to ${hours} hour${
                hours > 1 ? 's' : ''
              } of inactivity. You have been logged out.`;
        } else {
          this.timeoutMessage = `Your session timed out due to ${minutes} minute${
            minutes > 1 ? 's' : ''
          } of inactivity. You have been logged out.`;
        }
      }
    });

    // Auto slide
    setInterval(() => this.nextSlide(), 5000);
  }

  async login(formValue: any) {
    if (this.form.invalid) return;
    this.loading = true;

    try {
      const authTokenModel = await this.authService.login(formValue);
      if (authTokenModel) {
        const successLogin = await this.authService.loadUserSession();
        if (successLogin) {
          const returnUrl = this.storageService.get('returnUrl');
          const timeoutUsername = this.storageService.get('timeoutUsername');

          this.storageService.set('lastLoginUsername', formValue.username);

          if (returnUrl) {
            this.storageService.remove('returnUrl');
            
            if (timeoutUsername && timeoutUsername.toLowerCase() !== formValue.username.toLowerCase()) {
              this.storageService.remove('timeoutUsername');
              await this.router.navigate(['', 'landing']);
            } else {
              this.storageService.remove('timeoutUsername');
              await this.router.navigateByUrl(returnUrl);
            }
          } else {
            this.storageService.remove('timeoutUsername');
            await this.router.navigate(['', 'landing']);
          }
        }
      } else {
        this.notificationService.errorMessage('Login failed');
      }
      this.loading = false;
    } catch (e) {
      console.error('Login error', e);
      this.loading = false;
    }
  }

  // async login(formValue: any) {
  //   if (this.form.invalid) return;
  //   this.loading = true;

  //   try {
  //     // login now
  //     const authTokenModel = await this.authService.login(formValue);
  //     if (authTokenModel) {
  //       const successLogin = await this.authService.loadUserSession();
  //       if (successLogin) {
  //         const returnUrl = this.storageService.get('returnUrl');
  //         if (returnUrl) {
  //           this.storageService.remove('returnUrl');
  //           await this.router.navigateByUrl(returnUrl);
  //         } else {
  //           await this.router.navigate(['', 'landing']);
  //         }
  //       }
  //     } else {
  //       this.notificationService.errorMessage('Login failed');
  //     }
  //     this.loading = false;
  //   }).catch((e) => {
  //     console.error('Login error', e);
  //     this.loading = false;
  //   });
  // }

  toggleTheme() {
    this.isLightMode = !this.isLightMode;
    this.darkModeService.toggleDarkMode();
  }

  async sendResetLink() {
    if (!this.forgotEmail) {
      this.notificationService.errorMessage('Please enter your official email address');
      return;
    }
    this.forgotLoading = true;
    try {
      await this.authService.forgotPassword(this.forgotEmail);
      this.forgotSuccess = true;
    } catch (e) {
      console.error('Reset password error', e);
      this.notificationService.errorMessage('Could not send reset instructions. Please try again.');
    } finally {
      this.forgotLoading = false;
    }
  }

  nextSlide() {
    this.index = (this.index + 1) % this.slides.length;
  }

  prevSlide() {
    this.index = (this.index - 1 + this.slides.length) % this.slides.length;
  }
}
